import React, { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { historicalDataAPI, accountsAPI } from '../lib/api';
import { toast } from 'sonner';

import { WizardSteps } from './historical/WizardSteps';
import { ImportUploadForm, ImportList } from './historical/ImportSections';

export default function HistoricalImportWizard({ onComplete }) {
  const [imports, setImports] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImport, setActiveImport] = useState(null);
  const [step, setStep] = useState(0);
  const [processing, setProcessing] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [validationResult, setValidationResult] = useState(null);

  const [file, setFile] = useState(null);
  const [fiscalYear, setFiscalYear] = useState(new Date().getFullYear() - 1);
  const [importType, setImportType] = useState('trial_balance');

  const loadData = useCallback(async () => {
    try {
      const [importsRes, accountsRes] = await Promise.all([historicalDataAPI.list(), accountsAPI.getAll()]);
      setImports(importsRes.data);
      setAccounts(accountsRes.data);
    } catch (e) {
      console.error('Failed to load data:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleUpload = async () => {
    if (!file) return;
    setProcessing(true);
    try {
      const res = await historicalDataAPI.upload(file, fiscalYear, importType);
      setActiveImport(res.data);
      setStep(1);
      setFile(null);
      toast.success(`File parsed: ${res.data.parsed_rows} accounts found`);
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally { setProcessing(false); }
  };

  const handleMapAccounts = async () => {
    setProcessing(true);
    try {
      const res = await historicalDataAPI.mapAccounts(activeImport.id);
      setActiveImport(res.data);
      const mapped = res.data.mappings.filter(m => m.mapped_account_id).length;
      toast.success(`AI mapped ${mapped} of ${res.data.mappings.length} accounts`);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Mapping failed');
    } finally { setProcessing(false); }
  };

  const handleValidate = async () => {
    setProcessing(true);
    try {
      const res = await historicalDataAPI.validate(activeImport.id);
      if (res.data.valid) {
        setActiveImport(res.data.import_data || activeImport);
        setValidationResult({ valid: true });
        toast.success('Validation passed!');
        setStep(3);
      } else {
        setValidationResult(res.data);
        toast.error(`Validation failed: ${res.data.errors.length} issue(s)`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Validation failed');
    } finally { setProcessing(false); }
  };

  const handleConfirm = async () => {
    setProcessing(true);
    try {
      const res = await historicalDataAPI.confirm(activeImport.id);
      toast.success(`Opening balances posted! JE #${res.data.journal_entry_number}`);
      setActiveImport(null);
      setStep(0);
      setValidationResult(null);
      await loadData();
      onComplete?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to post opening balances');
    } finally { setProcessing(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setProcessing(true);
    try {
      await historicalDataAPI.delete(deleteTarget.id);
      toast.success('Import deleted');
      setDeleteTarget(null);
      if (activeImport?.id === deleteTarget.id) { setActiveImport(null); setStep(0); }
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally { setProcessing(false); }
  };

  const handleUpdateMapping = (idx, accountId) => {
    const updated = { ...activeImport };
    const acc = accounts.find(a => a.id === accountId);
    updated.mappings = [...updated.mappings];
    updated.mappings[idx] = {
      ...updated.mappings[idx],
      mapped_account_id: accountId || null,
      mapped_account_code: acc?.code || null,
      mapped_account_name: acc?.name || null,
    };
    setActiveImport(updated);
  };

  const handleSaveMappings = async () => {
    setProcessing(true);
    try {
      const mappings = activeImport.mappings.map(m => ({
        source_name: m.source_name, mapped_account_id: m.mapped_account_id,
      }));
      await historicalDataAPI.updateMappings(activeImport.id, mappings);
      toast.success('Mappings saved');
    } catch (e) {
      toast.error('Failed to save mappings');
    } finally { setProcessing(false); }
  };

  const resumeImport = (imp) => {
    setActiveImport(imp);
    setValidationResult(null);
    if (imp.status === 'confirmed') return;
    if (imp.status === 'validated') setStep(3);
    else if (imp.status === 'mapped') setStep(2);
    else if (imp.status === 'parsed') setStep(1);
    else setStep(0);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin text-[#D94E18]" /></div>;
  }

  if (activeImport && activeImport.status !== 'confirmed') {
    return (
      <WizardSteps
        activeImport={activeImport} step={step} setStep={setStep}
        processing={processing} accounts={accounts}
        validationResult={validationResult} setValidationResult={setValidationResult}
        onMapAccounts={handleMapAccounts} onSaveMappings={handleSaveMappings}
        onValidate={handleValidate} onConfirm={handleConfirm}
        onUpdateMapping={handleUpdateMapping}
        onCancel={() => { setActiveImport(null); setStep(0); setValidationResult(null); }}
      />
    );
  }

  return (
    <div className="space-y-6" data-testid="historical-import-section">
      <ImportUploadForm
        file={file} setFile={setFile} fiscalYear={fiscalYear} setFiscalYear={setFiscalYear}
        importType={importType} setImportType={setImportType}
        processing={processing} onUpload={handleUpload}
      />
      <ImportList
        imports={imports} onResume={resumeImport} onDelete={setDeleteTarget}
        deleteTarget={deleteTarget} setDeleteTarget={setDeleteTarget} onConfirmDelete={handleDelete}
      />
    </div>
  );
}
